import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Moon, Sun, User, LogOut, LogIn } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { MobileMenu } from './navbar/MobileMenu';
import { AuthModal } from './auth/AuthModal';

export function MobileTopBar() {
  const { isDark, toggle } = useTheme();
  const { user, signOut, showAuthModal, setShowAuthModal } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    setIsMenuOpen(false);
  };

  const navItems = [
    { to: '/', icon: null, text: 'Home', requiresAuth: false },
    { to: '/verses', icon: null, text: 'Verse Finder', requiresAuth: true },
    { to: '/daily-motivation', icon: null, text: 'Daily Motivation', requiresAuth: false },
    { to: '/monthly-readings', icon: null, text: 'Monthly Readings', requiresAuth: false },
    { to: '/bible', icon: null, text: 'Bible', requiresAuth: false },
    { to: '/trivia', icon: null, text: 'Bible Trivia', requiresAuth: true },
    { to: '/prayer-room', icon: null, text: 'Prayer Room', requiresAuth: false },
    { to: '/pray-4-me', icon: null, text: 'Pray 4 Me', requiresAuth: false },
    { to: '/contact', icon: null, text: 'Contact', requiresAuth: false }
  ];

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 md:hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center">
            <img
              src="/logo copy copy.png"
              alt="Verse4All"
              className="h-12 w-auto"
            />
          </Link>

          <div className="flex items-center space-x-2">
            {user && (
              <Link
                to="/profile"
                className="p-2 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Profile"
              >
                <User className="w-5 h-5" />
              </Link>
            )}

            <button
              onClick={toggle}
              className="p-2 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </button>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed top-[60px] left-0 right-0 bottom-0 z-40 bg-white dark:bg-gray-900 md:hidden overflow-y-auto"
          >
            <div className="container mx-auto px-4 py-6">
              <div className="space-y-2">
                {navItems.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setIsMenuOpen(false)}
                    className="block px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    {item.text}
                  </Link>
                ))}

                <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-800">
                  {user ? (
                    <>
                      <Link
                        to="/profile"
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center space-x-3 px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                      >
                        <User className="w-5 h-5" />
                        <span>Profile</span>
                      </Link>
                      <button
                        onClick={handleSignOut}
                        className="flex items-center space-x-3 w-full px-4 py-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                      >
                        <LogOut className="w-5 h-5" />
                        <span>Sign out</span>
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        setShowAuthModal(true);
                      }}
                      className="flex items-center space-x-3 w-full px-4 py-3 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                    >
                      <LogIn className="w-5 h-5" />
                      <span>Sign in</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
    </>
  );
}
